/**
 * Jargon-free banding lib — translates raw Keepa-derived metrics into
 * plain-English, ranged strings for customer-facing UI (Feed / Dossier /
 * Pipeline screens, see docs/plans/2026-08-12-category-first-brand-discovery-design.md
 * §4).
 *
 * Two reasons this exists:
 *  1. UX — customers shouldn't have to learn what "offer count" or "BSR"
 *     mean; they want "sells ~1–5k/mo", not a raw number.
 *  2. Keepa ToS hedge — surfacing derived bands rather than raw data points
 *     keeps us on the right side of Keepa's terms (see
 *     docs/legal/adapter-tos.md, Keepa section).
 *
 * Every function here is pure and total: garbage/missing input (null,
 * undefined-shaped, NaN, negative-where-impossible) always produces an
 * honest "unknown" fallback string — never throws.
 */

export function salesBand(monthlySold: number | null): string {
  if (monthlySold == null || !Number.isFinite(monthlySold) || monthlySold <= 0) {
    return 'sales unknown';
  }
  if (monthlySold < 100) return 'sells <100/mo';
  if (monthlySold < 500) return 'sells ~100–500/mo';
  if (monthlySold < 1000) return 'sells ~500–1k/mo';
  if (monthlySold < 5000) return 'sells ~1–5k/mo';
  return 'sells 5k+/mo';
}

export function sellerBand(offerCount: number | null): string {
  if (offerCount == null || !Number.isFinite(offerCount) || offerCount <= 0) {
    return 'sellers unknown';
  }
  if (offerCount === 1) return '1 seller';
  if (offerCount <= 5) return '2–5 sellers';
  if (offerCount <= 10) return '6–10 sellers';
  return '10+ sellers';
}

export function marginBand(pct: number | null): string {
  if (pct == null || !Number.isFinite(pct)) return 'margin unknown';
  if (pct < 0) return 'unprofitable at est. costs';
  if (pct < 15) return 'est. margin under 15%';
  if (pct < 25) return 'est. margin 15–25%';
  if (pct < 35) return 'est. margin 25–35%';
  return 'est. margin 35%+';
}

export function reviewBand(count: number | null): string {
  if (count == null || !Number.isFinite(count) || count < 0) return 'reviews unknown';
  if (count === 0) return 'no reviews yet';
  if (count < 50) return 'few reviews (<50)';
  if (count < 500) return 'moderately reviewed';
  return 'heavily reviewed';
}

const SIZE_BUCKET_LABEL: Record<'micro' | 'small' | 'medium' | 'large', string> = {
  micro: 'Micro brand',
  small: 'Small brand',
  medium: 'Medium brand',
  large: 'Large brand',
};

export function sizeLabel(
  bucket: 'micro' | 'small' | 'medium' | 'large' | null,
  productCount: number | null
): string {
  if (bucket == null) return 'Size unknown';
  const label = SIZE_BUCKET_LABEL[bucket];
  if (productCount == null || !Number.isFinite(productCount) || productCount <= 0) {
    return label;
  }
  return `${label} · ~${Math.round(productCount)} products`;
}

/** Formats a `catalog_products.price_cents` value for the dossier products table. */
export function price(cents: number | null): string {
  if (cents == null || !Number.isFinite(cents) || cents < 0) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

export function freshness(iso: string | null, now: Date = new Date()): string {
  if (!iso) return 'never checked';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 'never checked';

  const diffMs = now.getTime() - then.getTime();
  if (diffMs < MS_PER_HOUR) return 'checked just now';
  if (diffMs < 24 * MS_PER_HOUR) return 'checked today';
  if (diffMs < 48 * MS_PER_HOUR) return 'checked yesterday';
  if (diffMs < 7 * MS_PER_DAY) {
    const days = Math.floor(diffMs / MS_PER_DAY);
    return `checked ${days} days ago`;
  }

  // Fixed to UTC so the output doesn't drift with the host's local timezone
  // (input timestamps are UTC ISO strings from the API).
  const month = then.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  const day = then.getUTCDate();
  return `checked ${month} ${day}`;
}

/**
 * Days elapsed since `iso` (typically a claim's `updatedAt`), for the
 * Pipeline screen's "days in status" line. Same totality contract as
 * `freshness`: null/unparseable/future (clock skew) never throw.
 */
export function daysIn(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '—';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '—';

  const diffMs = now.getTime() - then.getTime();
  if (diffMs < MS_PER_DAY) return 'today';

  const days = Math.floor(diffMs / MS_PER_DAY);
  return days === 1 ? '1 day' : `${days} days`;
}

/**
 * Public Amazon listing URL for an ASIN (US marketplace — the pipeline only
 * sweeps domain 1 today; revisit when other domains ship).
 */
export function amazonListingUrl(asin: string): string {
  return `https://www.amazon.com/dp/${encodeURIComponent(asin)}`;
}

/**
 * Normalize a stored website value (often a bare registrable domain like
 * "example.com") into a clickable URL.
 */
export function externalUrl(hostOrUrl: string): string {
  return /^https?:\/\//.test(hostOrUrl) ? hostOrUrl : `https://${hostOrUrl}`;
}

/**
 * Customer-facing copy for FBA handling risks (guidance, not verdict —
 * title-classified; see keepa_brand_cache.handling_risks).
 */
const HANDLING_RISK_LABELS: Record<string, string> = {
  refrigerated: 'refrigerated — not FBA-eligible',
  meltable: 'meltable — seasonal FBA limits',
  glass_liquid: 'glass/liquid — check fees & breakage',
  short_shelf_life: 'short shelf life — expiry risk',
};

export function handlingRiskLabel(risk: string): string {
  return HANDLING_RISK_LABELS[risk] ?? risk;
}
